import { Component, inject, ViewChild, OnDestroy, OnInit } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { AndromedaMenuComponent } from '../andromeda-menu/andromeda-menu.component';
import { AndromedaButtonComponent } from '../andromeda-button/andromeda-button.component';
import { AndromedaButtonTypes } from '../../models/andromeda-button-types';
import { MatDialog } from '@angular/material/dialog';
import { Action } from '../../models/action';
import { AndromedaMenuOptionComponent } from '../andromeda-menu-option/andromeda-menu-option.component';
import { CommonModule } from '@angular/common';
import { firstValueFrom, Subscription } from 'rxjs';
import { EditLitigationDialogComponent } from '../edit-litigation-dialog/edit-litigation-dialog.component';
import { LitigationServiceEndpoints } from '@upassignations/feature';
import { ReportAnErrorDialogComponent } from '@upassignations/feature';
import { RowNode } from 'ag-grid-community';
import { ActCellIconType } from '../../models/act-cell-icon-type';
import { UiStateService } from '../../services/ui-state.service';

@Component({
  selector: 'lib-act-cell-name-renderer',
  imports: [
    AndromedaMenuComponent,
    AndromedaButtonComponent,
    AndromedaMenuOptionComponent,
    CommonModule,
  ],
  templateUrl: './act-cell-name-renderer.component.html',
  styleUrl: './act-cell-name-renderer.component.scss',
})
export class ActCellNameRendererComponent
  implements ICellRendererAngularComp, OnInit, OnDestroy
{
  @ViewChild('menus') menu: AndromedaMenuComponent;
  private dialog = inject(MatDialog);
  private uiStateService = inject(UiStateService);

  private activeIconSubscription: Subscription;

  menuOptions: MenuOptions[] = [];
  params: any;
  nameOfAct = '';
  litigation: any;
  uri: string;
  title: '';
  displayingInArchives: false;
  displayingInLogs: false;
  andromedaButtonTypes = AndromedaButtonTypes;
  infoIconActive = false;
  menuIconActive = false;
  cellId: string;
  isProcessing = false;
  isRowSelected = false;
  litigationDocumentIsDownloadable = false;

  // eslint-disable-next-line no-unused-vars
  showMetadata: (uri: string, params: any) => void;
  // eslint-disable-next-line no-unused-vars
  showActionsHistory: (uri: string, params: any) => void;
  // eslint-disable-next-line no-unused-vars
  deactivateLitigation: (uri: string, title: string) => void;
  // eslint-disable-next-line no-unused-vars
  reactivateLitigation: (uri: string, title: string) => void;
  // eslint-disable-next-line no-unused-vars
  isServiceDeactivated: (litigation: any, serviceEndpoint: string) => boolean;
  // eslint-disable-next-line no-unused-vars
  updateLitigation: (uri: string, updatedLitigation: any) => void;
  // eslint-disable-next-line no-unused-vars
  excludeLitigationFromGroup: (uri: string, title: string) => void;
  closeSidePanel: () => void;
  reportAnError: (
    litigationUri: string,
    message: string,
    subject: string
  ) => void;
  modifyLitigation: (
    type: string,
    litigationUri: string,
    newValueUri: string
  ) => void;
  // eslint-disable-next-line no-unused-vars
  unarchiveLitigation: (uri, litigationRow) => void;
  showListOfEntities: () => Promise<any[]>;
  showListOfProceduralActs: () => Promise<any[]>;
  showListOfTopics: () => Promise<any[]>;
  // eslint-disable-next-line no-unused-vars
  manuallySelectRow: (node: RowNode) => void;
  // eslint-disable-next-line no-unused-vars
  downloadLitigationDocument: (litigationUri) => void;
  rowId: string | null = null;

  ngOnInit(): void {
    this.subscribeToActiveIcon();
    this.subscribeToSelectedRow();
  }

  ngOnDestroy(): void {
    if (this.activeIconSubscription) {
      this.activeIconSubscription.unsubscribe();
    }
  }

  resetActiveStates(): void {
    this.menuIconActive = false;
    this.infoIconActive = false;
  }

  agInit(params: any): void {
    this.params = params;
    this.nameOfAct = params.value;
    this.litigation = params.data;
    this.uri = this.litigation.uri;
    this.title = this.litigation?.title;
    this.showMetadata = params.showMetadata;
    this.showActionsHistory = params?.showActionsHistory;
    this.deactivateLitigation = params?.deactivateLitigation;
    this.reactivateLitigation = params?.reactivateLitigation;
    this.isServiceDeactivated = params?.isServiceDeactivated;
    this.updateLitigation = params?.updateLitigation;
    this.excludeLitigationFromGroup = params?.excludeLitigationFromGroup;
    this.reportAnError = params?.reportAnError;
    this.modifyLitigation = params?.modifyLitigation;
    this.showListOfProceduralActs = params?.showListOfProceduralActs;
    this.showListOfTopics = params?.showListOfTopics;
    this.closeSidePanel = params?.closeSidePanel;
    this.unarchiveLitigation = params?.unarchiveLitigation;
    this.downloadLitigationDocument = params?.downloadLitigationDocument;

    this.displayingInArchives = params?.displayingInArchives;
    this.displayingInLogs = params?.displayingInLogs;

    this.manuallySelectRow = params.manuallySelectRow;

    this.cellId = `cell_${this.uri}`;

    this.initializeMenuOptions();
    if(!this.displayingInLogs) {
      this.initializeLitigationDocumentIsDownloadable();
    }
  }

  refresh(params: any): boolean {
    this.resetActiveStates();
    this.params = params;
    return true;
  }

  initializeLitigationDocumentIsDownloadable(): void {
    if (
        !this.isServiceDeactivated(
          this.litigation,
          LitigationServiceEndpoints.DOWNLOAD_LITIGATION_DOCUMENT
        )){          
      this.litigationDocumentIsDownloadable = true
    }
  }

  initializeMenuOptions(): void {
    const litigation = this.params.data;

    this.menuOptions = [];

    if(!this.displayingInLogs) {
      if (
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.HISTORY_LIST_OF_ACTION_LITIGATION
        )
      ) {
        this.menuOptions.push({
          label: "Voir l'historique des actions",
          action: Action.ActionsHistory,
        });
      }
    }

    if (this.displayingInArchives) {
      if (
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.UNARCHIVE_LITIGATION
        )
      )
        this.menuOptions.push({
          label: "Désarchiver l'acte",
          action: Action.Unarchive,
        });
    }

    if (!this.displayingInArchives && !this.displayingInLogs) {
      if (
        litigation.isDeprecated &&
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.REACTIVATE_LITIGATION
        )
      ) {
        this.menuOptions.push({
          label: "Réactiver l'acte",
          action: Action.Reactive,
        });
      }
      if (
        !litigation.isDeprecated &&
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.DEACTIVATE_LITIGATION
        )
      ) {
        this.menuOptions.push({
          label: "Désactiver l'acte",
          action: Action.Deactive,
        });
      }
      if (litigation.groupedWith) {
        this.menuOptions.push({
          label: "Exclure l'acte du groupe",
          action: Action.Exclude,
        });
      }
      if (
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.UPDATE_TOPIC
        )
      ) {
        this.menuOptions.push({
          label: 'Changer le thème',
          action: Action.ChangeTopic,
        });
      }

      if (
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.UPDATE_PROCEDURAL_ACT
        )
      ) {
        this.menuOptions.push({
          label: "Changer le type d'acte de procédure",
          action: Action.ChangeProceduralAct,
        });
      }
      if (
        !this.isServiceDeactivated(
          litigation,
          LitigationServiceEndpoints.REPORT_ISSUE
        )
      ) {
        this.menuOptions.push({
          label: 'Signaler une erreur',
          action: Action.ReportAnError,
        });
      }
    }
  }

  onClickOnOption(option: MenuOptions, event?): void {
    this.menu.optionHasBeenClicked(event);
    const litigation = this.params.data;

    switch (option.action) {
      case Action.ActionsHistory:
        this.uiStateService.clearActiveIcons();
        this.showActionsHistory(this.uri, this.params);

        break;
      case Action.ChangeTopic:
        this.openEditDialog('topic', litigation);

        break;
      case Action.ChangeProceduralAct:
        this.openEditDialog('proceduralAct', litigation);

        break;

      case Action.Deactive:
        this.deactivateLitigation(this.uri, this.title);

        break;
      case Action.Reactive:
        this.reactivateLitigation(this.uri, this.title);

        break;
      case Action.Exclude:
        this.excludeLitigationFromGroup(this.uri, this.title);

        break;
      case Action.ReportAnError:
        this.openReportAnErrorDialog(litigation);
        break;
      case Action.Unarchive:
        this.unarchiveLitigation(this.uri, litigation);
        break;
      default:
        break;
    }
  }

  async openEditDialog(type: string, litigation: any): Promise<void> {
    let options = [];
    let currentValue = null;
    let title = '';

    switch (type) {
      case 'topic':
        options = await this.showListOfTopics();
        currentValue = litigation.topic;
        title = 'Changer le thème';
        break;
      case 'proceduralAct':
        options = await this.showListOfProceduralActs();
        currentValue = litigation.proceduralAct;
        title = "Changer le type d'acte de procédure";
        break;

      default:
        return;
    }

    const dialogRef = this.dialog.open(EditLitigationDialogComponent, {
      width: '600px',
      data: {
        title,
        type,
        options,
        oldValue: currentValue,
        litigationUri: litigation.uri,
      },
    });
    const newValueUri = await firstValueFrom(dialogRef.afterClosed());
    if (newValueUri) {
      this.modifyLitigation(type, litigation.uri, newValueUri);
    }
  }
  async openReportAnErrorDialog(
    litigation: any
  ): Promise<{ subject: string; message: string } | undefined> {
    const dialogRef = this.dialog.open(ReportAnErrorDialogComponent, {
      width: '600px',
      data: {
        litigationTitle: litigation.title,
      },
    });
    const result = await firstValueFrom(dialogRef.afterClosed());

    if (result) {
      this.reportAnError(litigation.uri, result.message, result.subject);
    }
    return undefined;
  }
  showMetadataIfNotAlreadyDisplayed(): void {
    const currentActive = this.uiStateService.getCurrentActiveIcon();
    const isSameRowActive =
      currentActive?.cellId === this.cellId &&
      currentActive?.type === ActCellIconType.Info;
    const rowUri = `row_${this.uri}`;

    if (
      this.uiStateService.shouldDisplayMetadata(this.uri) &&
      !isSameRowActive
    ) {
      this.uiStateService.setLastDisplayedMetadataUri(this.uri);
      this.uiStateService.setSelectedRowId(rowUri);
      this.showMetadata(this.uri, this.params);
    }
  }
  checkIfRowHasBeenSelected(): boolean {
    if (this.rowId === `row_${this.uri}`) {
      return true;
    } else {
      return false;
    }
  }

  subscribeToActiveIcon(): void {
    this.activeIconSubscription = this.uiStateService.activeIcon$.subscribe(
      (active) => {
        if (!active) {
          this.menuIconActive = false;
          this.infoIconActive = false;
          return;
        }

        const { cellId, type } = active;

        if (this.cellId !== cellId) {
          this.menuIconActive = false;
          this.infoIconActive = false;
          return;
        }

        if (type === ActCellIconType.Info) {
          this.infoIconActive = true;
          this.menuIconActive = false;

          this.showMetadataIfNotAlreadyDisplayed();
        } else if (type === ActCellIconType.Menu) {
          this.menuIconActive = true;

          if (this.infoIconActive) {
            this.showMetadataIfNotAlreadyDisplayed();
          }
        }
      }
    );

    this.uiStateService.menuClosed$.subscribe(() => {
      this.menuIconActive = false;
    });
  }
  subscribeToSelectedRow(): void {
    this.uiStateService.selectedRowId$.subscribe((rowId) => {
      this.rowId = rowId;
    });
  }
  onMenuIconClick(): void {
    const current = this.uiStateService.getCurrentActiveIcon();
    const isInfoActiveElsewhere =
      current?.type === ActCellIconType.Info && current?.cellId !== this.cellId;
    const isInfoActiveHere =
      current?.cellId === this.cellId && current?.type === ActCellIconType.Info;
    const isMenuActiveHere =
      current?.cellId === this.cellId && current?.type === ActCellIconType.Menu;
    const rowUri = `row_${this.uri}`;

    this.manuallySelectRow(this.params.node);
    this.uiStateService.setSelectedRowId(rowUri);

    if (isInfoActiveElsewhere) {
      this.uiStateService.clearActiveIcons();
      this.uiStateService.clearLastDisplayedMetadataUri();
      this.uiStateService.setActiveIcon(this.cellId, ActCellIconType.Info);
      this.uiStateService.setActiveIcon(this.cellId, ActCellIconType.Menu);
    } else if (isMenuActiveHere) {
      this.uiStateService.clearMenuIconsOnly();
    } else {
      if (isInfoActiveHere) {
        this.uiStateService.setActiveIcon(this.cellId, ActCellIconType.Menu);
      } else {
        this.uiStateService.setActiveIcon(this.cellId, ActCellIconType.Menu);
      }
    }
  }
  onSeeMetadataClick(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    const currentActive = this.uiStateService.getCurrentActiveIcon();
    const isSameRowActive =
      currentActive?.cellId === this.cellId &&
      currentActive?.type === ActCellIconType.Info;
    const rowUri = `row_${this.uri}`;

    if (isSameRowActive) {
      this.infoIconActive = false;
      this.uiStateService.clearActiveIcons();
      this.closeSidePanel();
      this.uiStateService.clearLastDisplayedMetadataUri();
      setTimeout(() => (this.isProcessing = false), 900);
      return;
    }

    this.infoIconActive = true;
    this.menuIconActive = false;
    this.uiStateService.setActiveIcon(this.cellId, ActCellIconType.Info);
    this.uiStateService.setLastDisplayedMetadataUri(this.uri);
    this.uiStateService.setSelectedRowId(rowUri);
    this.showMetadata(this.uri, this.params);
    setTimeout(() => (this.isProcessing = false), 900);
  }

  onNameClick(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    if (this.litigationDocumentIsDownloadable){          
      this.downloadLitigationDocument(this.uri);
    }
  }

}

interface MenuOptions {
  label: string;
  action: Action;
  disabled?: boolean | (() => boolean);
}
